import styled, { css } from 'styled-components'

import Box from 'components/Box'
import Flex from 'components/Flex'
import InlineError from 'components/InlineError'
import RadioBox, { RadioProps } from 'components/RadioBox'
import { inputFocusBoxShadow } from 'components/TextInput/SimpleTextInput'
import Text from 'components/Text'
import { ErrorMessage, LabelText, RequiredText } from 'components/TextInput'

import { FixMeAny } from 'types/global'
import { KeyCode } from 'utils/constants/keyCode'
import { useTranslation } from 'react-i18next'

export type RadioOption = {
  label: RadioProps['component']
  value: RadioProps['value']
  hint?: RadioProps['hint']
  customComponent?: () => React.ReactNode
}

type Props = {
  fullWidth?: boolean
  gap?: string
  label?: string
  name?: string
  options?: RadioOption[]
  value?: string
  onChange?: (e: FixMeAny) => void
  isEditable?: boolean
  isVertical?: boolean
  isWrapped?: boolean
  placeholder?: string
  stretchOptions?: boolean
  alignItems?: 'left' | 'center' | 'right' | 'start' | 'end'
  required?: boolean
  error?: string
}

const RadioGroup = ({
  fullWidth,
  gap = '0.5rem',
  label,
  placeholder,
  name,
  value,
  options,
  onChange,
  isEditable = true,
  isVertical = false,
  isWrapped = false,
  stretchOptions = false,
  alignItems = isVertical ? 'start' : 'center',
  required,
  error,
}: Props) => {
  const { t } = useTranslation()

  const handleChange = (value?: string) => {
    onChange?.({
      target: {
        name: name ?? '',
        value,
      },
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, option: RadioOption) => {
    e.stopPropagation()

    if (e.key === KeyCode.enter) {
      handleChange(option?.value)
    }
  }

  const getOptionComponent = (option: RadioOption, index: number) => {
    const tabIndex = index + 1

    const optionComponent = (
      <>
        <RadioBox
          key={index}
          selected={option.value === value}
          component={typeof option.label === 'string' ? t(option.label) : option.label}
          disabled={!isEditable}
          isNoTab={isWrapped}
          tabIndex={tabIndex}
          value={option.value}
          onChange={() => isEditable && handleChange(option?.value)}
          error={error}
          hint={isWrapped ? undefined : option.hint}
        />
      </>
    )

    if (isWrapped) {
      const wrappedRadioButton = (
        <Flex isColumn gap="0.5rem" width={isVertical && stretchOptions ? '100%' : undefined}>
          <RadioButtonWrapper
            key={index}
            onKeyDownCapture={(e) => handleKeyDown(e, option)}
            selected={option.value === value}
            stretchOptions={stretchOptions}
            tabIndex={tabIndex}
            disabled={!isEditable}
            onClick={() => isEditable && handleChange(option?.value)}
            isVertical={isVertical}
          >
            {optionComponent}
          </RadioButtonWrapper>
          {option?.customComponent && (
            <CustomComponentWrapper visible={option.value === value}>{option.customComponent()}</CustomComponentWrapper>
          )}
        </Flex>
      )

      if (option.hint) {
        return (
          <Flex
            isColumn
            gap="0.5rem"
            flex={stretchOptions ? '1' : undefined}
            width={isVertical && stretchOptions ? '100%' : undefined}
          >
            <div>{wrappedRadioButton}</div>
            <Text color="disabledText">{option.hint}</Text>
          </Flex>
        )
      }

      return wrappedRadioButton
    }

    return optionComponent
  }

  return (
    <Box width={fullWidth ? '100%' : undefined}>
      {label && (
        <LabelText>
          {t(label)}
          {required && <RequiredText>*</RequiredText>}
        </LabelText>
      )}
      {placeholder && isEditable && <Text mb="0.5rem">{placeholder}</Text>}
      <Box display="flex" flexDirection={isVertical ? 'column' : 'row'} gap={gap} alignItems={alignItems}>
        {options && options?.length > 0 ? (
          options.map((option, index) => getOptionComponent(option, index))
        ) : (
          <InlineError title="no_option" />
        )}
      </Box>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Box>
  )
}

const CustomComponentWrapper = styled.div<{ visible: boolean }>`
  overflow: hidden;
  max-height: ${({ visible }) => (visible ? '300px' : '0px')};
  margin-left: 0.5rem;
  transition: max-height 0.35s ease;
`
const RadioButtonWrapper = styled.div<{
  selected?: boolean
  disabled?: boolean
  stretchOptions?: boolean
  isVertical?: boolean
}>`
  border-radius: 4px;
  outline: 1px solid ${({ theme, selected }) => (selected ? theme.colors.blue1 : theme.colors.gray3)};
  padding: 0.7rem;
  min-width: 4rem;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

  :hover {
    outline: 1px solid ${({ theme, selected }) => (selected ? theme.colors.blue2 : theme.colors.gray5)};
  }

  :focus {
    border: none;
    box-shadow: ${inputFocusBoxShadow};
    outline: 1px solid ${({ theme, selected }) => (selected ? theme.colors.blue1 : theme.colors.gray3)};
  }

  ${(p) =>
    p.isVertical &&
    p.stretchOptions &&
    css`
      width: 100%;
    `}

  ${(p) =>
    p.stretchOptions &&
    css`
      flex: 1;
    `}
`

export default RadioGroup
